import { Children, Fragment, isValidElement } from 'react'
import CitationMarker from '@/components/Citations/CitationMarker'
import { segmentCitationText } from '@/components/Citations/citationTextSegmenter'

/**
 * Turns `[1]` markers inside a node's own text children into `CitationMarker`s.
 *
 * This is the answer to "citations run inside text nodes, not over raw
 * markdown" (Stage 2.1). react-markdown hands each component override its
 * children with nested elements *already rendered* and text nodes as plain
 * strings, so applying this at every text-bearing override handles exactly the
 * text at that level — no recursion, no cloning, and nested `<strong>` text is
 * covered by the `strong` override doing the same thing.
 */
export function withCitations(children, citations, keyBase) {
  if (!citations?.length) return children

  return Children.map(children, (child, childIndex) => {
    if (typeof child !== 'string') return child
    const segments = segmentCitationText(child, citations)
    if (segments.length === 0) return child

    return segments.map((segment, index) => {
      const key = `${keyBase}-${childIndex}-${index}`
      if (segment.type === 'marker') {
        return (
          <CitationMarker
            key={key}
            marker={segment.marker}
            citation={segment.citation}
            targetIndex={segment.targetIndex}
          />
        )
      }
      return <Fragment key={key}>{segment.value}</Fragment>
    })
  })
}

/** True when a node's children are a single code element, i.e. a fenced block. */
export function isFencedCode(children) {
  const only = Children.toArray(children)[0]
  return Children.count(children) === 1 && isValidElement(only) && only.props?.node?.tagName === 'code'
}
